// UI-2 layer operations as command builders (applied via the store like everything else).
import type { Doc, Layer } from "./entities";
import { newId } from "./document";
import type { Command } from "./commands";

const SWATCHES = ["#e8eaf0", "#4dd0e1", "#8ab4f8", "#e0b34d", "#4caf7d", "#e05c5c", "#c58af8", "#f8a48a"];

export function makeAddLayerCommand(doc: Doc): Command {
  const layer: Layer = {
    id: newId(),
    name: `Layer ${doc.layers.length + 1}`,
    color: SWATCHES[doc.layers.length % SWATCHES.length]!,
    visible: true,
    locked: false,
    opacity: 1,
    print: true
  };
  return { kind: "addLayer", layer, index: doc.layers.length };
}

/**
 * UI-2: delete a layer; entities migrate to `targetLayerId` (or are deleted when null).
 * EC-UI-3: refuses to delete the last layer.
 */
export function makeDeleteLayerCommand(doc: Doc, layerId: string, targetLayerId: string | null): Command {
  if (doc.layers.length <= 1) throw new Error("layers: cannot delete the last layer");
  const index = doc.layers.findIndex((l) => l.id === layerId);
  const layer = doc.layers[index];
  if (!layer) throw new Error(`layers: unknown layer ${layerId}`);
  const ids = [...(doc.zOrder[layerId] ?? [])];
  const commands: Command[] = [];
  if (ids.length > 0) {
    if (targetLayerId) {
      commands.push({
        kind: "reorder",
        moves: ids.map((id) => ({
          id,
          from: { layerId, index: doc.zOrder[layerId]!.indexOf(id) },
          to: { layerId: targetLayerId, index: -1 }
        }))
      });
      // group children keep their own layerId field; migrate those too
      const childPatches: { id: string; before: Record<string, unknown>; after: Record<string, unknown> }[] =
        [];
      for (const entity of Object.values(doc.entities)) {
        if (entity.layerId === layerId && !ids.includes(entity.id)) {
          childPatches.push({ id: entity.id, before: { layerId }, after: { layerId: targetLayerId } });
        }
      }
      if (childPatches.length > 0) commands.push({ kind: "updateEntities", patches: childPatches });
    } else {
      commands.push({
        kind: "deleteEntities",
        removed: ids.map((id) => ({
          entity: structuredClone(doc.entities[id]!),
          z: { layerId, index: doc.zOrder[layerId]!.indexOf(id) }
        }))
      });
    }
  }
  commands.push({ kind: "deleteLayer", layer: structuredClone(layer), index });
  return { kind: "batch", label: `delete layer ${layer.name}`, commands };
}

export function makeUpdateLayerCommand(doc: Doc, layerId: string, changes: Partial<Layer>): Command {
  const layer = doc.layers.find((l) => l.id === layerId);
  if (!layer) throw new Error(`layers: unknown layer ${layerId}`);
  const before: Partial<Layer> = {};
  for (const key of Object.keys(changes) as (keyof Layer)[]) {
    (before as Record<string, unknown>)[key] = layer[key];
  }
  return { kind: "updateLayer", id: layerId, before, after: changes };
}

export function makeReorderLayerCommand(doc: Doc, layerId: string, to: number): Command {
  const from = doc.layers.findIndex((l) => l.id === layerId);
  if (from < 0) throw new Error(`layers: unknown layer ${layerId}`);
  return { kind: "reorderLayer", id: layerId, from, to: Math.max(0, Math.min(to, doc.layers.length - 1)) };
}
